/**
 * BillingPage — the Billing Work Queue (wireframe 3).
 *
 * REV-040/041/042/043/044/048 + INT-006. The finance command surface:
 *   - Revenue summary bar (captured this week / pending approval / submitted /
 *     write-downs) from GET /revenue/billing-queue.
 *   - Per-hospital breakdown (captured / pending / overdue) with color-coded
 *     status (StatusBadge).
 *   - An invoice work queue with stage tabs (Invoicing / Pending Approval /
 *     Submitted / Overdue), per-row approve/submit + a read-receipt action
 *     (POST /invoices/{id}:receipt).
 *   - A charges view (GET /charges) with variance/exception flags + the EHR
 *     835/837 validation summary (GET /charges/validation, REV-041).
 *   - A QuickBooks sync-log panel (GET /integration/quickbooks/sync-log).
 *   - Right context: AR aging (GET /revenue/ar-aging) + an AI insight card.
 *
 * Invoice generation lives in the right-context "Generate" card so the existing
 * REV-042 generate flow keeps working. Every panel degrades gracefully when its
 * Phase 3 endpoint isn't wired yet (friendly empty/disabled state, never crash).
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  useBilling,
  type BillingQueueHospital,
  type BillingQueueInvoice,
  type ChargeListItem,
  type QuickBooksSyncEntry,
} from '../hooks/useBilling';
import { useArAging, useRevenue } from '../hooks/useRevenue';
import { hospitalLabel } from '../components/nav';
import { colors, radius } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { RoleImpactStrip } from '../components/PlatformImpactStrip';
import { RevenueProjectionCard } from '../components/RevenueProjectionCard';
import { ThreePanel } from '../components/ThreePanel';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { EmptyState } from '../components/EmptyState';
import { AIInsightCard } from '../components/AIInsightCard';
import { useShellSlot } from '../components/shellContext';

function money(amount: number | undefined | null): string {
  if (amount === undefined || amount === null || Number.isNaN(amount)) return '—';
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

type StageTab = 'invoicing' | 'pending_approval' | 'submitted' | 'overdue';

const STAGE_TABS: { key: StageTab; label: string }[] = [
  { key: 'invoicing', label: 'Invoicing' },
  { key: 'pending_approval', label: 'Pending Approval' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'overdue', label: 'Overdue' },
];

/**
 * Bucket an invoice's lifecycle `stage` (InvoiceStatus) into a UI stage tab.
 * Submitted invoices that are aging past terms (days_outstanding > 30) fall
 * into the Overdue tab.
 */
function tabForInvoice(inv: BillingQueueInvoice): StageTab {
  switch (inv.stage) {
    case 'draft':
      return 'invoicing';
    case 'in_review':
    case 'approved':
      return 'pending_approval';
    case 'submitted':
    case 'partial':
      return (inv.days_outstanding ?? 0) > 30 ? 'overdue' : 'submitted';
    case 'paid':
    case 'void':
      return 'submitted';
    default:
      return 'invoicing';
  }
}

/** Infer a per-hospital semantic status from its overdue/pending balances. */
function hospitalTier(h: BillingQueueHospital): {
  tier: 'success' | 'warning' | 'danger';
  label: string;
} {
  if ((h.overdue ?? 0) > 0) return { tier: 'danger', label: 'Overdue' };
  if ((h.pending ?? 0) > 0) return { tier: 'warning', label: 'Watch' };
  return { tier: 'success', label: 'Current' };
}

export function BillingPage() {
  const {
    queue,
    charges,
    validation,
    syncLog,
    receipted,
    queueAvailable,
    chargesAvailable,
    syncLogAvailable,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    approveInvoice,
    submitInvoice,
    recordReceipt,
  } = useBilling();
  const aging = useArAging();
  const generate = useRevenue();
  const [stage, setStage] = useState<StageTab>('invoicing');
  const [bottomTab, setBottomTab] = useState<'charges' | 'sync'>('charges');
  const [caseId, setCaseId] = useState('');

  useShellSlot({ breadcrumb: 'Billing' });

  useEffect(() => {
    aging.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { summary, by_hospital: hospitals, invoices } = queue;

  /** Group queue invoices by UI stage tab. */
  const byStage = useMemo(() => {
    const groups: Record<StageTab, BillingQueueInvoice[]> = {
      invoicing: [],
      pending_approval: [],
      submitted: [],
      overdue: [],
    };
    for (const inv of invoices) groups[tabForInvoice(inv)].push(inv);
    return groups;
  }, [invoices]);

  const stageRows = byStage[stage];

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = caseId.trim();
    if (!trimmed) return;
    const created = await generate.generateInvoice(trimmed);
    if (created) {
      setCaseId('');
      refresh();
    }
  };

  /** Overdue total for the AI insight (St. Mary's = hosp-001 by convention). */
  const stMarys = hospitals.find((h) => h.tenant_id === 'hosp-001');
  const aiOverdue = stMarys?.overdue ?? 0;
  const aiHospital = stMarys?.hospital ?? hospitalLabel('hosp-001');

  const invoiceColumns: Column<BillingQueueInvoice>[] = [
    {
      key: 'invoice',
      header: 'Invoice',
      render: (inv) => <span style={{ fontWeight: 600 }}>{inv.invoice_id}</span>,
    },
    { key: 'hospital', header: 'Hospital', render: (inv) => <span style={muted}>{inv.hospital}</span> },
    {
      key: 'patient',
      header: 'Patient',
      render: (inv) => <span style={muted}>{inv.patient ?? '—'}</span>,
    },
    { key: 'status', header: 'Status', render: (inv) => <StatusBadge value={inv.stage} /> },
    { key: 'amount', header: 'Amount', align: 'right', render: (inv) => money(inv.amount) },
    {
      key: 'days',
      header: 'Days out',
      align: 'right',
      render: (inv) => (
        <span style={{ color: (inv.days_outstanding ?? 0) > 30 ? colors.danger : undefined }}>
          {inv.days_outstanding ?? 0}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (inv) => {
        const canApprove = inv.stage === 'draft';
        const canSubmit = inv.stage === 'in_review' || inv.stage === 'approved';
        const isReceipted = receipted.has(inv.invoice_id);
        const canReceipt =
          (inv.stage === 'submitted' || inv.stage === 'partial' || inv.stage === 'paid') &&
          !isReceipted;
        return (
          <div style={actions}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => approveInvoice(inv.invoice_id)}
              disabled={isWorking || !canApprove}
            >
              Approve
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => submitInvoice(inv.invoice_id)}
              disabled={isWorking || !canSubmit}
            >
              Submit
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => recordReceipt(inv.invoice_id)}
              disabled={isWorking || !canReceipt}
              title={isReceipted ? 'Read-receipt recorded' : 'Record read-receipt'}
            >
              {isReceipted ? '✓ Receipt' : 'Receipt'}
            </button>
          </div>
        );
      },
    },
  ];

  const hospitalColumns: Column<BillingQueueHospital>[] = [
    {
      key: 'hospital',
      header: 'Hospital',
      render: (h) => <span style={{ fontWeight: 600 }}>{h.hospital}</span>,
    },
    { key: 'captured', header: 'Captured', align: 'right', render: (h) => money(h.captured) },
    { key: 'pending', header: 'Pending', align: 'right', render: (h) => money(h.pending) },
    {
      key: 'overdue',
      header: 'Overdue',
      align: 'right',
      render: (h) => (
        <span style={{ color: (h.overdue ?? 0) > 0 ? colors.danger : undefined, fontWeight: 600 }}>
          {money(h.overdue)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (h) => {
        const { tier, label } = hospitalTier(h);
        return <StatusBadge status={tier} label={label} />;
      },
    },
  ];

  /** Lookup variance reason/delta for a charge from the validation summary. */
  const varianceFor = (chargeId: string) =>
    validation?.variances.find((v) => v.charge_id === chargeId);

  const chargeColumns: Column<ChargeListItem>[] = [
    {
      key: 'charge',
      header: 'Charge',
      render: (c) => <span style={{ fontWeight: 600 }}>{c.charge_id}</span>,
    },
    { key: 'case', header: 'Case', render: (c) => <span style={muted}>{c.case_id}</span> },
    { key: 'source', header: 'Source', render: (c) => <span style={muted}>{c.source}</span> },
    { key: 'amount', header: 'Amount', align: 'right', render: (c) => money(c.amount) },
    {
      key: 'variance',
      header: 'Variance',
      align: 'right',
      render: (c) => {
        const v = varianceFor(c.charge_id);
        return v ? (
          <span style={{ color: colors.danger, fontWeight: 600 }} title={v.reason}>
            {money(v.delta)}
          </span>
        ) : (
          <span style={muted}>—</span>
        );
      },
    },
    {
      key: 'status',
      header: 'Flag',
      render: (c) => {
        const v = varianceFor(c.charge_id);
        if (c.status === 'exception' || c.variance_flag) {
          return <StatusBadge status="danger" label={v?.reason ?? 'Exception'} />;
        }
        return <StatusBadge value={c.status} />;
      },
    },
  ];

  const syncColumns: Column<QuickBooksSyncEntry>[] = [
    {
      key: 'type',
      header: 'Type',
      render: (s) => <StatusBadge value={s.type} status="info" label={s.type.replace(/_/g, ' ')} />,
    },
    { key: 'ref', header: 'Reference', render: (s) => <span style={{ fontWeight: 600 }}>{s.ref}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (s) =>
        s.error ? <StatusBadge status="danger" label={s.status} /> : <StatusBadge value={s.status} />,
    },
    { key: 'when', header: 'Synced', render: (s) => <span style={muted}>{fmtDateTime(s.at)}</span> },
  ];

  return (
    <>
      <PageHeader
        title="Billing Work Queue"
        subtitle="Capture, review, submit, and reconcile revenue across hospitals (REV-040/041/042/043/044/048, INT-006)."
        actions={
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              refresh();
              aging.refresh();
            }}
            disabled={isLoading}
          >
            Refresh
          </button>
        }
      />

      <RoleImpactStrip />

      {error && (
        <p style={errorText} role="alert">
          {error.message}
        </p>
      )}
      {actionError && (
        <p style={errorText} role="alert">
          {actionError.message}
        </p>
      )}

      {/* Revenue summary bar */}
      <div style={summaryBar}>
        <SummaryStat label="Captured this week" value={money(summary.captured_this_week)} tone="action" />
        <SummaryStat label="Pending approval" value={money(summary.pending_approval)} tone="warning" />
        <SummaryStat label="Submitted" value={money(summary.submitted)} tone="success" />
        <SummaryStat label="Write-downs" value={money(summary.write_downs)} tone="danger" />
      </div>
      {!queueAvailable && (
        <p style={noticeText}>
          Live billing-queue figures aren’t connected in this environment yet — totals populate once
          the revenue billing-queue endpoint is wired.
        </p>
      )}

      {/* Forward-looking revenue projection (AI-004) — surfaces the forecast to
          Finance, not just the admin Intelligence page. */}
      <div style={{ marginBottom: 20 }}>
        <RevenueProjectionCard />
      </div>

      <ThreePanel
        primary={
          <>
            {/* Per-hospital breakdown */}
            <Card title="Per-hospital breakdown" padded={false}>
              {isLoading ? (
                <div style={{ padding: 18 }}>
                  <LoadingIndicator text="Loading…" />
                </div>
              ) : !queueAvailable && hospitals.length === 0 ? (
                <EmptyState
                  title="Breakdown not available yet"
                  description="Per-hospital captured / pending / overdue totals appear here once the billing-queue endpoint is wired."
                />
              ) : (
                <DataTable
                  columns={hospitalColumns}
                  rows={hospitals}
                  rowKey={(h) => h.tenant_id}
                  emptyTitle="No hospital activity"
                  emptyDescription="Captured revenue by hospital appears here."
                />
              )}
            </Card>

            {/* Invoice work queue with stage tabs */}
            <Card
              title="Invoice work queue"
              padded={false}
              actions={
                <div style={tabBar} role="tablist" aria-label="Invoice stages">
                  {STAGE_TABS.map((s) => (
                    <button
                      key={s.key}
                      type="button"
                      role="tab"
                      aria-selected={stage === s.key}
                      onClick={() => setStage(s.key)}
                      style={{ ...tabBtn, ...(stage === s.key ? tabActive : {}) }}
                    >
                      {s.label}
                      {byStage[s.key].length > 0 && (
                        <span style={countPill}>{byStage[s.key].length}</span>
                      )}
                    </button>
                  ))}
                </div>
              }
            >
              {isWorking && (
                <div style={{ padding: 12 }}>
                  <LoadingIndicator text="Working…" />
                </div>
              )}
              {!queueAvailable && invoices.length === 0 ? (
                <EmptyState
                  title="Invoice queue not available yet"
                  description="Generate an invoice from the right panel, or wait for the billing-queue endpoint to be wired."
                />
              ) : (
                <DataTable
                  columns={invoiceColumns}
                  rows={stageRows}
                  rowKey={(inv) => inv.invoice_id}
                  emptyTitle={`No ${STAGE_TABS.find((s) => s.key === stage)?.label.toLowerCase()} invoices`}
                  emptyDescription="Invoices move through stages as they are approved, submitted, and reconciled."
                />
              )}
            </Card>

            {/* Charges + QuickBooks sync log */}
            <Card
              title="Source reconciliation"
              padded={false}
              actions={
                <div style={tabBar} role="tablist" aria-label="Reconciliation views">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={bottomTab === 'charges'}
                    onClick={() => setBottomTab('charges')}
                    style={{ ...tabBtn, ...(bottomTab === 'charges' ? tabActive : {}) }}
                  >
                    Charges
                    {validation && validation.variances.length > 0 && (
                      <span style={{ ...countPill, background: colors.danger, color: '#fff' }}>
                        {validation.variances.length}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={bottomTab === 'sync'}
                    onClick={() => setBottomTab('sync')}
                    style={{ ...tabBtn, ...(bottomTab === 'sync' ? tabActive : {}) }}
                  >
                    QuickBooks sync log
                  </button>
                </div>
              }
            >
              {bottomTab === 'charges' ? (
                <>
                  {validation && (
                    <div style={validationBar}>
                      <span style={{ color: colors.success, fontWeight: 600 }}>
                        {validation.matched} matched
                      </span>
                      <span style={{ color: colors.textMuted }}>·</span>
                      <span
                        style={{
                          color: validation.variances.length > 0 ? colors.danger : colors.textSecondary,
                          fontWeight: 600,
                        }}
                      >
                        {validation.variances.length} variance(s)
                      </span>
                      <span style={muted}>EHR 835/837 reconciliation (REV-041)</span>
                    </div>
                  )}
                  {!chargesAvailable && charges.length === 0 ? (
                    <EmptyState
                      title="Charges not available yet"
                      description="Captured charges with variance/exception flags appear here once the charges endpoint is wired (REV-040)."
                    />
                  ) : (
                    <DataTable
                      columns={chargeColumns}
                      rows={charges}
                      rowKey={(c) => c.charge_id}
                      emptyTitle="No charges captured"
                      emptyDescription="Charges synced from finance appear here, flagged for variance/exceptions."
                    />
                  )}
                </>
              ) : !syncLogAvailable && syncLog.length === 0 ? (
                <EmptyState
                  title="QuickBooks not connected"
                  description="The QuickBooks integration sync log appears here once INT-006 is wired."
                />
              ) : (
                <DataTable
                  columns={syncColumns}
                  rows={syncLog}
                  rowKey={(s) => s.id}
                  emptyTitle="No sync activity"
                  emptyDescription="QuickBooks invoice-push / payment-pull runs appear here (INT-006)."
                />
              )}
            </Card>
          </>
        }
        rightContext={
          <>
            <AIInsightCard heading="Escalation recommended" confidence={0.86} acceptLabel="Escalate">
              {aiOverdue > 0 ? (
                <>
                  {aiHospital} has {money(aiOverdue)} in overdue invoices aging past terms. Recommend
                  escalating to the CFO contact and queueing read-receipt follow-ups.
                </>
              ) : (
                <>
                  Recommend escalating {aiHospital}’s overdue invoices — they are aging toward the AR
                  threshold. Confirm to route a finance follow-up.
                </>
              )}
            </AIInsightCard>

            <Card title="Generate invoice" surface>
              <form onSubmit={handleGenerate} style={generateForm}>
                <input
                  type="text"
                  value={caseId}
                  onChange={(e) => setCaseId(e.target.value)}
                  placeholder="Case ID (e.g. case-001)"
                  className="input"
                  aria-label="Case ID"
                  style={{ flex: 1 }}
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  disabled={generate.isWorking || !caseId.trim()}
                >
                  Generate
                </button>
              </form>
              {generate.error && (
                <p style={errorText} role="alert">
                  {generate.error.message}
                </p>
              )}
            </Card>

            <div>
              <div style={summaryTitle}>AR aging</div>
              {aging.isLoading && <LoadingIndicator text="Loading…" />}
              {aging.error && (
                <p style={errorText} role="alert">
                  {aging.error.message}
                </p>
              )}
              {aging.report && (
                <>
                  <div style={agingHeadline}>{money(aging.report.total_outstanding)}</div>
                  <div style={agingSub}>
                    outstanding across {aging.report.outstanding_invoice_count} invoice(s) · as of{' '}
                    {aging.report.as_of}
                  </div>
                  <dl style={bucketList}>
                    {aging.report.buckets.map((b) => (
                      <div key={b.label} style={bucketRow}>
                        <dt style={bucketLabel}>{b.label}</dt>
                        <dd style={bucketValue}>
                          {money(b.balance)} <span style={muted}>· {b.invoice_count}</span>
                        </dd>
                      </div>
                    ))}
                  </dl>
                </>
              )}
              {!aging.isLoading && !aging.report && !aging.error && (
                <p style={muted}>No AR aging data.</p>
              )}
            </div>
          </>
        }
      />
    </>
  );
}

function SummaryStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'action' | 'warning' | 'success' | 'danger';
}) {
  const toneColor = {
    action: colors.action,
    warning: colors.warning,
    success: colors.success,
    danger: colors.danger,
  }[tone];
  return (
    <div style={statCard}>
      <div style={{ ...statAccent, background: toneColor }} />
      <div style={statBody}>
        <div style={statLabel}>{label}</div>
        <div style={statValue}>{value}</div>
      </div>
    </div>
  );
}

const summaryBar: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
  marginBottom: 16,
};
const statCard: CSSProperties = {
  display: 'flex',
  background: colors.white,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  overflow: 'hidden',
};
const statAccent: CSSProperties = { width: 4, flexShrink: 0 };
const statBody: CSSProperties = { padding: '12px 16px' };
const statLabel: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
};
const statValue: CSSProperties = {
  fontSize: '1.4rem',
  fontWeight: 700,
  color: colors.textPrimary,
  marginTop: 2,
};
const tabBar: CSSProperties = { display: 'flex', gap: 4 };
const tabBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  background: 'none',
  border: 'none',
  borderBottom: '2px solid transparent',
  fontSize: '0.82rem',
  fontWeight: 600,
  color: colors.textSecondary,
  cursor: 'pointer',
};
const tabActive: CSSProperties = { color: colors.action, borderBottom: `2px solid ${colors.action}` };
const countPill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 18,
  height: 18,
  padding: '0 5px',
  borderRadius: 999,
  background: colors.surfaceAlt,
  color: colors.textSecondary,
  fontSize: '0.7rem',
  fontWeight: 700,
};
const validationBar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 18px',
  borderBottom: `1px solid ${colors.surfaceAlt}`,
  fontSize: '0.84rem',
};
const generateForm: CSSProperties = { display: 'flex', gap: 8, alignItems: 'center' };
const actions: CSSProperties = { display: 'flex', gap: 6 };
const muted: CSSProperties = { color: colors.textSecondary, fontSize: '0.86rem' };
const errorText: CSSProperties = { color: colors.danger, fontSize: '0.9rem', marginBottom: 12 };
const noticeText: CSSProperties = { color: colors.textSecondary, fontSize: '0.84rem', marginBottom: 16 };
const summaryTitle: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
  marginBottom: 8,
};
const agingHeadline: CSSProperties = { fontSize: '1.6rem', fontWeight: 700, color: colors.textPrimary };
const agingSub: CSSProperties = { fontSize: '0.8rem', color: colors.textSecondary, marginBottom: 12 };
const bucketList: CSSProperties = { margin: 0, display: 'flex', flexDirection: 'column' };
const bucketRow: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  padding: '7px 0',
  borderBottom: `1px solid ${colors.surfaceAlt}`,
};
const bucketLabel: CSSProperties = { fontSize: '0.84rem', color: colors.textSecondary };
const bucketValue: CSSProperties = { margin: 0, fontSize: '0.86rem', fontWeight: 600 };
